import { Alert, Box, Skeleton, Typography } from "@mui/material";
import { OUTPUT_DATA_TYPE, PREEXEC_ACTION_KINDS } from "../../../constants";

import { BACKEND_ROUTES } from "../../../backendRoutes";
import { DownloadCommandPanel } from "../../alignment-report/DownloadProjectButton";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import { MODEL_PROPTYPES } from "../../../models";
import PropTypes from "prop-types";
import { getMissionLocalFolderPath } from "../../alignment-report/getMissionLocalFolderPath";
import useSWR from "swr";

ModuleDownloadContent.propTypes = {
    process: PropTypes.shape(MODEL_PROPTYPES.Process).isRequired,
    moduleVersion: PropTypes.shape(MODEL_PROPTYPES.ModuleVersion).isRequired,
};

export function ModuleDownloadContent({ process, moduleVersion }) {
    const {
        data: pipelineInstance,
        error: pipelineInstanceFetchError,
        isLoading: pipelineInstanceIsLoading,
    } = useSWR(
        `${BACKEND_ROUTES.PIPELINE_INSTANCE}/${process.pipelineInstanceUuid}`
    );

    const missionUuid =
        pipelineInstance?.translateParams?.variables?.missionUuid;

    const {
        data: mission,
        error: missionFetchError,
        isLoading: missionIsLoading,
    } = useSWR(missionUuid ? `${BACKEND_ROUTES.MISSION}/${missionUuid}` : null);

    // Without a step name the download command would carry no filter and fetch
    // the outputs of every step of the pipeline instance, silently. Only
    // processes created outside of a translated pipeline lack one, so refuse
    // rather than build a command that quietly returns the wrong files.
    if (!process.stepName)
        return (
            <Alert severity="error">
                Cannot determine the step of this process.
            </Alert>
        );

    const mergedFetchError = pipelineInstanceFetchError ?? missionFetchError;
    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;

    const isLoading = pipelineInstanceIsLoading || missionIsLoading;
    if (isLoading)
        return (
            <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height="10em"
            />
        );

    if (!mission)
        return <Alert severity="error">Cannot determine HCC mission.</Alert>;

    // We do not give the pipeline instance to the function since it is not necessarily
    // an RGB fast ortho pipeline instance required by the function.
    const outputFolder = getMissionLocalFolderPath(mission) ?? ".";

    const downloadItems = buildDownloadItems(
        moduleVersion,
        pipelineInstance,
        missionUuid,
        process.stepName
    );

    const hasItems = Object.keys(downloadItems).length > 0;

    return (
        <Box sx={{ p: 1 }}>
            <Typography variant="subtitle1" sx={{ p: 1 }}>
                Download module results. Copy the command to launch in{" "}
                <Box
                    sx={{
                        display: "inline",
                        p: 0.5,
                        borderRadius: 1,
                        bgcolor: (theme) => theme.palette.grey[210],
                    }}
                >
                    cmd
                </Box>
                .
            </Typography>
            {hasItems ? (
                <DownloadCommandPanel
                    itemsToDownload={downloadItems}
                    outputFolder={outputFolder}
                />
            ) : (
                <Alert severity="error">
                    No downloadable outputs found for this module.
                </Alert>
            )}
        </Box>
    );
}

const OUTPUT_TYPE_TO_KIND = {
    [OUTPUT_DATA_TYPE.FILE]: PREEXEC_ACTION_KINDS.DOWNLOAD_FILE,
    [OUTPUT_DATA_TYPE.LOG]: PREEXEC_ACTION_KINDS.DOWNLOAD_FILE,
    [OUTPUT_DATA_TYPE.FOLDER]: PREEXEC_ACTION_KINDS.DOWNLOAD_FILE,
    [OUTPUT_DATA_TYPE.FILES]: PREEXEC_ACTION_KINDS.DOWNLOAD_FILES,
};

const BOUND_BINARIES_TYPE_TO_KIND = {
    [OUTPUT_DATA_TYPE.FILE]: PREEXEC_ACTION_KINDS.DOWNLOAD_FILES,
    [OUTPUT_DATA_TYPE.FOLDER]: PREEXEC_ACTION_KINDS.DOWNLOAD_FILES,
};

const buildDownloadItems = (
    moduleVersion,
    pipelineInstance,
    missionUuid,
    stepName
) => {
    // Outputs are filtered by step rather than by module: a pipeline instance
    // can run the same module as several steps, and only the step name tells
    // their results apart.
    const outputs = moduleVersion.definition?.outputs;

    if (!outputs) return {};

    const items = {};
    const piEndpoint = `/pipeline-instances/${pipelineInstance.uuid}/output-binary-data`;
    const piOutputDataEndpoint = `/pipeline-instances/${pipelineInstance.uuid}/output-data`;

    if (missionUuid) {
        items["sessionMetadataJson"] = {
            kind: PREEXEC_ACTION_KINDS.BUILD_SESSION_METADATA,
            missionUuid,
            to: "session-metadata.json",
        };
    }

    for (const [outputName, output] of Object.entries(outputs)) {
        const type = output.type;

        // Skip rename outputs
        if (type === OUTPUT_DATA_TYPE.RENAME_FILE) continue;

        if (type === OUTPUT_DATA_TYPE.CSV) {
            items[outputName] = {
                kind: PREEXEC_ACTION_KINDS.BUILD_RESULT_FILE,
                endpoint: piOutputDataEndpoint,
                args: {
                    stepName,
                    rejected: false,
                },
                to: getResolvedOutputValue(
                    pipelineInstance,
                    stepName,
                    outputName
                ),
            };
        } else {
            const kind = OUTPUT_TYPE_TO_KIND[type];
            if (!kind) continue;

            // A "Files" output is uploaded as one binary per globbed file, each
            // named after its own file name rather than after the output. Those
            // binaries are only reachable by filtering on the binary type
            // "File".
            const args =
                type === OUTPUT_DATA_TYPE.FILES
                    ? { stepName, type: OUTPUT_DATA_TYPE.FILE }
                    : { stepName, name: outputName };

            items[outputName] = {
                kind,
                endpoint: piEndpoint,
                args,
                from: "s3Location",
                to: "relativePath",
            };
        }

        // Process boundBinaries if present
        if (output.boundBinaries) {
            for (const [binaryName, binary] of Object.entries(
                output.boundBinaries
            )) {
                const actionKind = BOUND_BINARIES_TYPE_TO_KIND[binary.type];
                if (!actionKind) continue;

                items[binaryName] = {
                    kind: actionKind,
                    endpoint: piEndpoint,
                    args: {
                        stepName,
                        name: binaryName,
                    },
                    from: "s3Location",
                    to: "relativePath",
                };
            }
        }
    }

    return items;
};

function getResolvedOutputValue(pipelineInstance, stepName, outputName) {
    // A job is named after the step it runs, so its name is the step name.
    // Matching on it rather than on the module version, which several jobs of
    // one pipeline instance can share.
    const theJob = pipelineInstance.translatedPipeline?.spec?.jobs?.find(
        (job) => job.name === stepName
    );

    const postExecSpecsString = theJob?.postExec?.args?.[0];

    if (!postExecSpecsString) return undefined;

    const postExecSpecs = JSON.parse(postExecSpecsString);

    return postExecSpecs.uploadResults?.[outputName]?.value;
}
